import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { useState } from "react";
import { EquipmentSelectionDialog } from "./EquipmentSelectionDialog";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels, getEquipmentLabel } from "@/i18n/labels";

interface EquipmentSectionProps {
  equipment: string[];
  vehicleType: "car" | "mc";
  onEquipmentSelect: (equipment: string[]) => void;
}

export const EquipmentSection = ({
  equipment,
  vehicleType = "car",
  onEquipmentSelect,
}: EquipmentSectionProps) => {
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const [showDialog, setShowDialog] = useState(false);
  const [showAllSelected, setShowAllSelected] = useState(false);

  const handleRemoveEquipment = (itemToRemove: string) => {
    const newEquipment = equipment.filter((item) => item !== itemToRemove);
    onEquipmentSelect(newEquipment);
  };

  const displayedEquipment = showAllSelected
    ? equipment
    : equipment.slice(0, 10);

  return (
    <div className="space-y-3">
      {/* Selected equipment badges */}
      {equipment.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">
            {ct.dialogs.verifyVehicleInfo.selectedEquipment}
          </p>
          <div className="flex flex-wrap gap-2">
            {displayedEquipment.map((item) => (
              <Badge
                key={item}
                variant="secondary"
                className="flex items-center gap-1 pr-1 text-xs"
              >
                <span>{getEquipmentLabel(item, language)}</span>
                <button
                  onClick={() => handleRemoveEquipment(item)}
                  className="ml-1 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                  type="button"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {equipment.length > 10 && !showAllSelected && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => setShowAllSelected(true)}
              >
                {ct.dialogs.verifyVehicleInfo.showMoreEquipment.replace("{count}", String(equipment.length - 10))}
              </Button>
            )}
            {showAllSelected && equipment.length > 10 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => setShowAllSelected(false)}
              >
                {ct.dialogs.verifyVehicleInfo.showLess}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Select equipment button */}
      <Button
        variant="outline"
        onClick={() => setShowDialog(true)}
        className="w-full"
      >
        {ct.dialogs.verifyVehicleInfo.selectEquipment}
      </Button>

      {/* Equipment selection dialog */}
      <EquipmentSelectionDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        selectedEquipment={equipment}
        onEquipmentChange={onEquipmentSelect}
        vehicleType={vehicleType}
      />
    </div>
  );
};
